/**
 * Workspace · Transfer detail — voucher-document layout.
 *
 * Renders one `kind=transfer` cash movement as a printable voucher with
 * sticky composer head, state ribbon, 60/40 doc-stage (paper + 6-card
 * rail), linked-records strip, and activity timeline. Append-only —
 * voiding posts a new reversal row per ADR-0012 §3.
 *
 * @see prototype/cash/transfer-detail.html — design contract
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §2 (kind=transfer), §6 (Dr/Cr posting)
 */
import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  Banknote,
  Check,
  ChevronDown,
  Copy,
  CreditCard,
  ExternalLink,
  FileText,
  Printer,
  RotateCcw,
  Shield,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { Alert, Card, CardBody } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import {
  SubtypePill,
  classifySubtype,
  SUBTYPE_LABEL,
  type BankAccountType,
  type TransferSubtype,
} from "../../../../features/transfers/index.ts";
import { VoidMovementDialog } from "../../../../features/petty-cash/index.ts";
import { paiseToInr, formatDate, formatDateTime } from "../../../../lib/format.ts";
import { openDocPdf } from "../../../../lib/pdf.ts";

export const Route = createFileRoute("/(workspace)/cash/transfers/$movementId")({
  params: {
    parse: (raw) => ({ movementId: z.string().uuid().parse(raw.movementId) }),
    stringify: (parsed) => ({ movementId: parsed.movementId }),
  },
  component: TransferDetailRoute,
});

function TransferDetailRoute() {
  const { movementId } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const movementQ = trpc.cashMovements.get.useQuery({ movementId });
  const accountsQ = trpc.bankAccounts.list.useQuery({ includeArchived: true });
  // Approval threshold — pulled from petty-cash anchor (companies row).
  // Hook called unconditionally before any early returns.
  const anchorQ = trpc.pettyCash.anchorData.useQuery({});

  const [voidOpen, setVoidOpen] = useState(false);

  const reverseMut = trpc.cashMovements.reverse.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cashMovements.get.invalidate({ movementId }),
        utils.cashMovements.list.invalidate(),
        utils.cashMovements.transferStats.invalidate(),
        utils.pettyCash.anchorData.invalidate(),
        utils.briefings.workspaceSignals.invalidate(),
        utils.bankAccounts.list.invalidate(),
      ]);
      setVoidOpen(false);
      void navigate({ to: "/cash/transfers" });
    },
  });

  if (movementQ.isLoading || accountsQ.isLoading) {
    return (
      <DetailShell>
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading transfer…</p>
          </CardBody>
        </Card>
      </DetailShell>
    );
  }

  if (movementQ.error) {
    const isNotFound = movementQ.error.data?.code === "NOT_FOUND";
    return (
      <DetailShell>
        <Alert
          intent="bad"
          title={isNotFound ? "Transfer not found." : "Couldn't load transfer."}
          description={movementQ.error.message}
        />
      </DetailShell>
    );
  }

  const m = movementQ.data?.movement;
  if (!m) return null;

  // ── Account joins (for type chips + balance computations) ──────────
  const accountsById = new Map<
    string,
    {
      id: string;
      name: string;
      type: BankAccountType;
      accountNumberLast4: string | null;
      runningBalancePaise: string;
      imprestAmountPaise: string | null;
      creditLimitPaise: string | null;
      bankDetailsChangedAt: string | null;
    }
  >();
  for (const a of accountsQ.data?.rows ?? []) {
    accountsById.set(a.id, {
      id: a.id,
      name: a.name,
      type: a.type as BankAccountType,
      accountNumberLast4: a.accountNumberLast4,
      runningBalancePaise: a.runningBalancePaise,
      imprestAmountPaise: a.imprestAmountPaise,
      creditLimitPaise: a.creditLimitPaise,
      bankDetailsChangedAt: a.bankDetailsChangedAt,
    });
  }
  const fromAcct = m.fromAccountId ? (accountsById.get(m.fromAccountId) ?? null) : null;
  const toAcct = m.toAccountId ? (accountsById.get(m.toAccountId) ?? null) : null;

  const subtype: TransferSubtype | null =
    fromAcct && toAcct ? classifySubtype(fromAcct.type, toAcct.type) : null;

  const isReversal = m.reversesMovementId !== null;
  const isReversed = m.reversedByMovementId !== null;
  const isLocked = isReversal || isReversed;

  // Balance before/after — approximation. `runningBalancePaise` is the
  // *current* live balance; for the most-recent movement, before = after
  // ± amount. If newer rows landed since this voucher, the values drift;
  // the design accepts that gap (the V1.x bank-statement reconciler
  // hardens it).
  const amount = BigInt(m.amountPaise);
  const fromAfter = fromAcct ? BigInt(fromAcct.runningBalancePaise) : null;
  const fromBefore = fromAfter !== null ? fromAfter + amount : null;
  const toAfter = toAcct ? BigInt(toAcct.runningBalancePaise) : null;
  const toBefore = toAfter !== null ? toAfter - amount : null;

  // 100% imprest fill if to-account is cash with a target
  const imprestPct =
    toAcct && toAcct.type === "cash" && toAcct.imprestAmountPaise
      ? clamp(Number(((toAfter ?? 0n) * 100n) / BigInt(toAcct.imprestAmountPaise)), 0, 100)
      : null;

  // ── Cooling-off (24h from bankDetailsChangedAt on either leg) ───────
  const now = Date.now();
  const coolingOff =
    isWithin24h(fromAcct?.bankDetailsChangedAt ?? null, now) ||
    isWithin24h(toAcct?.bankDetailsChangedAt ?? null, now);

  const approvalThresholdPaise = anchorQ.data?.approvalThresholdPaise
    ? BigInt(anchorQ.data.approvalThresholdPaise)
    : 5_000_000n; // ₹50,000 fallback per system-decisions §4
  const founderReviewRequired = amount > approvalThresholdPaise;

  // ── Composed strings ───────────────────────────────────────────────
  const fromName = fromAcct?.name ?? m.fromAccountName ?? "—";
  const toName = toAcct?.name ?? m.toAccountName ?? "—";
  const voucherNumber = `TRN-${m.valueDate.replace(/-/g, "")}-${m.id.slice(0, 4).toUpperCase()}`;
  const internalId = `cash-movement-${m.id.slice(0, 8)}`;

  // Ribbon tone — emerald (default), rose (voided), amber (this is the reversal row)
  const ribbonTone: "ok" | "bad" | "warn" = isReversed ? "bad" : isReversal ? "warn" : "ok";

  return (
    <DetailShell>
      {/* ── Composer head (sticky) ───────────────────────────────── */}
      <header className="sticky top-0 z-20 bg-paper/95 backdrop-blur border-b border-line flex flex-wrap items-center gap-3 px-6 py-2.5 min-h-[60px]">
        <Link
          to="/cash/transfers"
          className="inline-grid place-items-center w-9 h-9 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg no-underline shrink-0"
          aria-label="Back to transfers"
        >
          <ArrowLeft size={16} strokeWidth={2.2} />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="m-0 flex items-center gap-2 flex-wrap text-base font-bold tracking-tight leading-tight text-ink">
            <span>Transfer voucher</span>
            <span className="font-mono text-xs font-medium px-2 py-0.5 rounded-full bg-raised text-muted">
              {voucherNumber}
            </span>
            <StatusPill kind={isReversed ? "voided" : isReversal ? "reversal" : "posted"} />
          </h1>
          <p className="mt-0.5 text-xs text-muted leading-snug flex flex-wrap items-center gap-x-1">
            <strong className="text-ink-2 font-semibold">{fromName}</strong>
            <ArrowRight size={11} strokeWidth={2.2} className="text-muted" aria-hidden />
            <strong className="text-ink-2 font-semibold">{toName}</strong>
            <span aria-hidden className="text-line-2 mx-1">
              ·
            </span>
            <span className="num text-ink font-medium">{paiseToInr(m.amountPaise)}</span>
            <span aria-hidden className="text-line-2 mx-1">
              ·
            </span>
            <span className="num text-ink">{formatDate(m.valueDate)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => openDocPdf(`/api/cash-movements/${m.id}/pdf`)}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm border border-line bg-paper text-ink text-xs font-semibold hover:bg-bg"
          >
            <Printer size={13} strokeWidth={2} />
            Print
          </button>
          {fromAcct && toAcct ? (
            <Link
              to="/cash/transfers/new"
              search={{ from: fromAcct.id, to: toAcct.id }}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm border border-line bg-paper text-ink text-xs font-semibold hover:bg-bg no-underline"
            >
              <Copy size={13} strokeWidth={2} />
              Duplicate
            </Link>
          ) : null}
          {!isLocked ? (
            <div className="inline-flex rounded-sm overflow-hidden shadow-sm">
              <button
                type="button"
                onClick={() => setVoidOpen(true)}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 bg-warn text-paper text-xs font-semibold hover:opacity-90"
              >
                <RotateCcw size={13} strokeWidth={2.2} />
                Void this transfer
              </button>
              <button
                type="button"
                aria-label="Other actions"
                disabled
                className="h-9 px-2.5 bg-warn text-paper border-l border-paper/20 grid place-items-center opacity-70 cursor-not-allowed"
              >
                <ChevronDown size={12} strokeWidth={2.4} />
              </button>
            </div>
          ) : null}
        </div>
      </header>

      {/* ── Page body ─────────────────────────────────────────────── */}
      <main className="px-6 pt-5 pb-24 max-w-[1320px] w-full mx-auto">
        {/* State ribbon */}
        <StateRibbon
          tone={ribbonTone}
          isReversed={isReversed}
          isReversal={isReversal}
          reversedByShort={m.reversedByMovementId?.slice(0, 8) ?? null}
          reversesShort={m.reversesMovementId?.slice(0, 8) ?? null}
        />

        {reverseMut.error ? (
          <div className="mb-4">
            <Alert
              intent="bad"
              title="Couldn't void this transfer"
              description={reverseMut.error.message}
            />
          </div>
        ) : null}

        {/* Doc stage — 60/40 grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-[18px] items-start mb-[18px]">
          {/* LEFT — the voucher */}
          <article
            className="bg-paper border border-line rounded shadow-card overflow-hidden"
            aria-label="Transfer voucher"
          >
            {/* Doc bar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-line bg-raised">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100 text-xs font-bold mr-2">
                <ArrowLeftRight size={10} strokeWidth={2.4} />
                Transfer voucher · own-to-own
              </span>
              <span className="text-xs text-muted font-mono mr-auto overflow-hidden text-ellipsis whitespace-nowrap">
                <strong className="text-ink-2 font-sans font-semibold">{voucherNumber}</strong>
                {" · "}
                {internalId}
              </span>
              <button
                type="button"
                onClick={() => openDocPdf(`/api/cash-movements/${m.id}/pdf`)}
                className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-sm text-xs font-medium text-muted hover:bg-bg hover:text-ink"
                title="Print this voucher"
              >
                <Printer size={12} strokeWidth={2} />
                Print slip
              </button>
            </div>

            {/* Paper */}
            <div className="px-11 pt-9 pb-7">
              {/* Letterhead */}
              <header className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-6 items-start pb-4 border-b-2 border-ink">
                <div>
                  <div className="w-11 h-11 rounded-sm bg-sky-50 text-sky-700 border border-sky-100 grid place-items-center mb-2">
                    <Wallet size={22} strokeWidth={2} />
                  </div>
                  <h2 className="m-0 text-lg font-bold text-ink tracking-tight">
                    Sharma Industries Pvt Ltd
                  </h2>
                  <div className="mt-1 text-xs text-muted leading-snug">
                    Internal transfer · own-to-own asset shuffle
                  </div>
                </div>
                <div className="sm:text-right font-mono">
                  <div className="text-xs font-bold tracking-[0.18em] text-muted mb-1 uppercase">
                    Transfer Voucher
                  </div>
                  <h1 className="m-0 text-2xl font-extrabold tracking-tight text-ink leading-none">
                    {monthHeader(m.valueDate)}
                  </h1>
                  <div className="mt-2 text-xs text-ink-2">
                    <span className="text-muted font-sans mr-1">Voucher #</span>
                    {voucherNumber}
                  </div>
                  {subtype ? (
                    <div className="mt-0.5 text-xs text-ink-2">
                      <span className="text-muted font-sans mr-1">Subtype</span>
                      {SUBTYPE_LABEL[subtype]}
                    </div>
                  ) : null}
                  <div className="mt-3 inline-block px-2.5 py-1 bg-bg border border-dashed border-line-2 rounded-sm text-xs text-muted font-sans">
                    Internal · <strong className="text-ink-2 font-mono">{internalId}</strong>
                  </div>
                </div>
              </header>

              {/* From / To meta grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-5 border-b border-line">
                <PartyCell
                  direction="from"
                  name={fromName}
                  type={fromAcct?.type ?? null}
                  accountNumberLast4={fromAcct?.accountNumberLast4 ?? null}
                />
                <PartyCell
                  direction="to"
                  name={toName}
                  type={toAcct?.type ?? null}
                  accountNumberLast4={toAcct?.accountNumberLast4 ?? null}
                  imprestPaise={toAcct?.imprestAmountPaise ?? null}
                />
              </div>

              {/* Dates strip — 4 cells */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-3 py-4 border-b border-line">
                <DateCell
                  label="Value date"
                  value={formatDate(m.valueDate)}
                  sub={weekdayHint(m.valueDate)}
                />
                <DateCell
                  label="Posted at"
                  value={timeOnly(m.createdAt)}
                  sub={isoStamp(m.createdAt)}
                />
                <DateCell
                  label="Posted by"
                  value={shortenActor(m.createdByActorId)}
                  sub="Recorded actor"
                  plainFont
                />
                <DateCell label="Reference" value="—" sub="Not required for own-to-own" muted />
              </div>

              {/* The single transfer line */}
              <table className="w-full border-collapse mt-4 text-xs">
                <thead>
                  <tr>
                    <th className="text-left py-2 pr-1.5 pl-0 text-xs font-bold uppercase tracking-wider text-muted border-b border-line">
                      Description
                    </th>
                    <th className="text-left py-2 px-1.5 text-xs font-bold uppercase tracking-wider text-muted border-b border-line">
                      Subtype
                    </th>
                    <th className="text-right py-2 pl-1.5 pr-0 text-xs font-bold uppercase tracking-wider text-muted border-b border-line">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className="py-3.5 pr-1.5 pl-0 align-middle text-ink-2"
                      style={{ width: "56%" }}
                    >
                      <strong className="block text-sm font-semibold text-ink leading-snug">
                        {m.narration}
                      </strong>
                      <span className="block text-xs text-muted mt-0.5">
                        Append-only money movement · ADR-0012 §6 balanced posting.
                      </span>
                    </td>
                    <td className="py-3.5 px-1.5 align-middle">
                      {subtype ? (
                        <SubtypePill subtype={subtype} />
                      ) : (
                        <span className="text-xs text-muted">—</span>
                      )}
                    </td>
                    <td className="py-3.5 pl-1.5 pr-0 text-right num text-ink font-semibold text-sm">
                      {paiseToInr(m.amountPaise)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Totals — note + stack */}
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_280px] gap-[18px] pt-[18px] border-t border-line mt-1">
                <div className="px-3.5 py-3 bg-raised rounded-sm text-xs text-muted leading-relaxed">
                  <strong className="text-ink-2">Asset shuffle — no GL net impact.</strong> A
                  transfer changes <em>where</em> money sits, not <em>how much</em> the company has.
                  Posts as a single{" "}
                  <code className="font-mono text-xs bg-ink/5 px-1 rounded text-ink-2">
                    cash_movements.kind=transfer
                  </code>{" "}
                  row with both source and destination set — see ADR-0012 §6.
                </div>
                <div className="px-1">
                  <div className="flex justify-between py-1 text-xs text-muted">
                    <span>Amount transferred</span>
                    <span className="num text-ink-2 font-semibold">
                      {paiseToInr(m.amountPaise)}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 text-xs text-muted">
                    <span>Fees / charges</span>
                    <span className="num text-ink-2 font-semibold">{paiseToInr(0n)}</span>
                  </div>
                  <div className="flex justify-between mt-1.5 pt-2.5 border-t border-line text-sm font-bold text-ink">
                    <span>Total transferred</span>
                    <span className="num text-base font-extrabold">
                      {paiseToInr(m.amountPaise)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Doc foot — posting preview */}
              <div className="pt-3.5 mt-3.5 border-t border-line grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-4 text-xs text-muted leading-relaxed">
                <div>
                  Per <strong className="text-ink-2">ADR-0012 §6</strong> this projects to the
                  General Ledger as a single balanced Dr/Cr pair. The destination sub-ledger
                  increases by the same paise that the source decreases — net zero against the chart
                  of accounts.{" "}
                  <code className="font-mono text-xs bg-ink/5 px-1 rounded text-ink-2">
                    cash_movements.kind = transfer
                  </code>
                  .
                </div>
                <div className="text-right font-mono">
                  <div className="text-muted font-sans text-xs mb-0.5">GL posting preview</div>
                  <div>
                    <span className="text-ok font-semibold">Dr</span>{" "}
                    <strong className="text-ink-2 font-sans">{toName}</strong>{" "}
                    {paiseToInr(m.amountPaise)}
                  </div>
                  <div>
                    <span className="text-bad font-semibold">Cr</span>{" "}
                    <strong className="text-ink-2 font-sans">{fromName}</strong>{" "}
                    {paiseToInr(m.amountPaise)}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* RIGHT — sticky rail */}
          <aside
            className="flex flex-col gap-3 lg:sticky lg:top-[80px] self-start"
            aria-label="Voucher detail rail"
          >
            {/* 1 · Posting */}
            <RailCard tone="sky" title="Posting" mini="read-only">
              <div className="text-xs text-ink-2 leading-relaxed">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-xs text-muted">cash_movements.kind</span>
                  <span className="font-mono text-xs px-1.5 py-0.5 bg-paper border border-sky-100 rounded-sm text-sky-700 font-semibold">
                    transfer
                  </span>
                </div>
                <div className="flex flex-col gap-1.5 p-2.5 bg-paper border border-sky-100 rounded-sm">
                  <GlRow side="dr" label={toName} amount={paiseToInr(m.amountPaise)} />
                  <GlRow side="cr" label={fromName} amount={paiseToInr(m.amountPaise)} />
                </div>
                <div className="mt-2.5 text-xs text-muted">
                  Computed by domain · never editable.{" "}
                  <span className="text-sky-700 font-medium">ADR-0012 §6 →</span>
                </div>
              </div>
            </RailCard>

            {/* 2 · From account */}
            {fromAcct ? (
              <PartyRailCard
                direction="from"
                account={fromAcct}
                amountPaise={m.amountPaise}
                balanceBefore={fromBefore}
                balanceAfter={fromAfter}
              />
            ) : null}

            {/* 3 · To account */}
            {toAcct ? (
              <PartyRailCard
                direction="to"
                account={toAcct}
                amountPaise={m.amountPaise}
                balanceBefore={toBefore}
                balanceAfter={toAfter}
                imprestPct={imprestPct}
              />
            ) : null}

            {/* 4 · Impact summary */}
            <RailCard title="Impact summary">
              <ImpactRow label="Total transferred" value={paiseToInr(m.amountPaise)} />
              <ImpactRow
                label="Approval threshold"
                value={paiseToInr(approvalThresholdPaise)}
                muted
              />
              <ImpactBoolRow label="Founder review required" yes={founderReviewRequired} />
              <ImpactBoolRow label="Cooling-off in effect" yes={coolingOff} />
              <div className="flex justify-between items-baseline mt-1 pt-2.5 border-t border-line px-3.5 text-sm font-bold text-ink">
                <span>Net to destination</span>
                <span className="num text-ok text-base font-extrabold">
                  +{paiseToInr(m.amountPaise)}
                </span>
              </div>
            </RailCard>

            {/* 5 · Routing */}
            <RailCard title="Routing">
              <ol className="list-none m-0 p-0">
                <RoutingStep
                  state="done"
                  who="Voucher posted"
                  role="By the recording actor"
                  when={timeOnly(m.createdAt)}
                />
                <RoutingStep
                  state="done"
                  who="Internal validation passed"
                  role={
                    <>
                      <code className="font-mono text-xs bg-raised px-1 rounded">ADR-0012 §6</code>{" "}
                      balanced Dr/Cr
                    </>
                  }
                  when={timeOnly(m.createdAt)}
                />
                <RoutingStep
                  state="done"
                  who="Balances updated"
                  role="Both legs invalidated"
                  when={timeOnly(m.createdAt)}
                  last
                />
              </ol>
            </RailCard>

            {/* 6 · Audit trail */}
            <RailCard title="Audit trail">
              <AuditMetaRow k="Voucher #" v={voucherNumber} />
              <AuditMetaRow k="Internal ID" v={internalId} />
              <AuditMetaRow k="Created at" v={isoStamp(m.createdAt)} />
              <AuditMetaRow k="Posted by" v={shortenActor(m.createdByActorId)} />
              <AuditMetaRow
                k="Reversed by"
                v={m.reversedByMovementId ? m.reversedByMovementId.slice(0, 8) + "…" : "—"}
                mutedValue={!m.reversedByMovementId}
              />
              <div className="mt-2.5 pt-2.5 border-t border-line text-xs text-muted">
                Append-only —{" "}
                <Link
                  to="/records/audit-log"
                  className="text-sky-700 font-medium no-underline hover:underline"
                >
                  full audit chain →
                </Link>
              </div>
            </RailCard>
          </aside>
        </div>

        {/* ── Linked records strip (4 cells) ─────────────────────── */}
        <section
          className="mb-[18px] bg-paper border border-line rounded shadow overflow-hidden"
          aria-label="Linked records"
        >
          <div className="px-4 py-3 border-b border-line flex items-center gap-2.5">
            <ExternalLink size={18} className="text-muted" aria-hidden />
            <h3 className="m-0 text-sm font-semibold text-ink">Linked records · 4</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
            <LinkedCell
              tone="sky"
              icon={fromAcct?.type === "cash" ? <Wallet size={14} /> : <Banknote size={14} />}
              kicker="From account"
              value={
                fromAcct
                  ? `${shorten(fromAcct.name, 20)}${fromAcct.accountNumberLast4 ? ` ···${fromAcct.accountNumberLast4}` : ""}`
                  : "—"
              }
              sub={fromAcct ? `${fromAcct.type}` : "Unknown"}
              {...(fromAcct
                ? {
                    to: "/cash/bank-accounts/$bankAccountId" as const,
                    params: { bankAccountId: fromAcct.id },
                  }
                : {})}
            />
            <LinkedCell
              tone="ok"
              icon={toAcct?.type === "cash" ? <Wallet size={14} /> : <Banknote size={14} />}
              kicker="To account"
              value={
                toAcct
                  ? `${shorten(toAcct.name, 20)}${toAcct.accountNumberLast4 ? ` ···${toAcct.accountNumberLast4}` : ""}`
                  : "—"
              }
              sub={toAcct ? toAcct.type : "Unknown"}
              {...(toAcct
                ? {
                    to: "/cash/bank-accounts/$bankAccountId" as const,
                    params: { bankAccountId: toAcct.id },
                  }
                : {})}
            />
            <LinkedCell
              tone="ok"
              icon={<Shield size={14} />}
              kicker="Audit entry"
              value={`audit-${m.id.slice(0, 6)}`}
              sub={`cash_movement.posted · ${timeOnly(m.createdAt)}`}
              to="/records/audit-log"
            />
            <LinkedCell
              tone="muted"
              icon={<FileText size={14} />}
              kicker="Statement match"
              value="Not matched yet"
              sub="Lands with V1.x reconciler"
              mutedValue
            />
          </div>
        </section>

        {/* ── Activity timeline ─────────────────────────────────── */}
        <section
          className="bg-paper border border-line rounded shadow overflow-hidden"
          aria-label="Voucher activity"
        >
          <div className="px-5 py-3.5 border-b border-line flex items-center justify-between">
            <div>
              <h3 className="m-0 text-sm font-semibold">Activity on this voucher</h3>
              <p className="m-0 mt-0.5 text-xs text-muted">Append-only · 4 events since posting</p>
            </div>
          </div>
          <ol className="list-none p-0 m-0 px-5 pb-3.5">
            <li className="py-4 pl-1 text-xs font-semibold uppercase tracking-wider text-muted">
              Today · {formatDate(m.createdAt)}
            </li>
            <TimelineItem
              tone="ok"
              icon={<Check size={9} strokeWidth={2.4} />}
              what={`Voucher posted · ${paiseToInr(m.amountPaise)}`}
              when={timeOnly(m.createdAt)}
              who={shortenActor(m.createdByActorId)}
              body={
                <>
                  Created and committed in one step.{" "}
                  <span className="num text-ink-2 font-medium">{paiseToInr(m.amountPaise)}</span>{" "}
                  moves from {fromName} to {toName}.{" "}
                  {founderReviewRequired
                    ? "Above founder threshold — flagged for review."
                    : "Within founder threshold — no approval queue."}
                </>
              }
            />
            <TimelineItem
              tone="sky"
              icon={<Wallet size={9} strokeWidth={2.4} />}
              what={`Destination balance updated · ${toName}`}
              when={timeOnly(m.createdAt)}
              who="System"
              body={
                <>
                  <code className="font-mono text-xs bg-raised px-1 rounded text-ink-2">
                    bank_accounts.running_balance
                  </code>{" "}
                  recomputed from the cash-movements window per ADR-0012 §6.
                </>
              }
            />
            <TimelineItem
              tone="sky"
              icon={<Banknote size={9} strokeWidth={2.4} />}
              what={`Source balance updated · ${fromName}`}
              when={timeOnly(m.createdAt)}
              who="System"
              body={
                <>
                  Running balance dropped by{" "}
                  <span className="num text-ink-2 font-medium">{paiseToInr(m.amountPaise)}</span>.
                  Statement match remains pending until the next bank-feed pull.
                </>
              }
            />
            <TimelineItem
              tone="sky"
              icon={<Shield size={9} strokeWidth={2.4} />}
              what={
                <>
                  Audit entry written ·{" "}
                  <code className="font-mono text-xs bg-raised px-1 rounded text-ink-2">
                    cash_movement.posted
                  </code>
                </>
              }
              when={timeOnly(m.createdAt)}
              who="System"
              body={
                <>
                  Audit row{" "}
                  <code className="font-mono text-xs bg-raised px-1 rounded text-ink-2">
                    audit-{m.id.slice(0, 6)}
                  </code>{" "}
                  committed via{" "}
                  <code className="font-mono text-xs bg-raised px-1 rounded text-ink-2">
                    recordAudit
                  </code>
                  .{" "}
                  <Link
                    to="/records/audit-log"
                    className="text-sky-700 font-medium no-underline hover:underline"
                  >
                    View entry →
                  </Link>
                </>
              }
              last
            />
          </ol>
        </section>

        {reverseMut.isSuccess ? (
          <div className="mt-4">
            <Alert
              intent="info"
              title="Voided."
              description="The reversal posts as a new append-only row per ADR-0012 §3."
            />
          </div>
        ) : null}
      </main>

      <VoidMovementDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title="Void this transfer?"
        amountLabel={paiseToInr(m.amountPaise)}
        kindLabel="Transfer"
        pending={reverseMut.isPending}
        {...(reverseMut.error ? { errorMessage: reverseMut.error.message } : {})}
        onConfirm={(reason) => reverseMut.mutate({ movementId, reason })}
      />
    </DetailShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Layout shell — strips the default workspace padding so the composer
// head can span full width and the body has its own max-width container.
// ────────────────────────────────────────────────────────────────────

function DetailShell({ children }: { children: React.ReactNode }) {
  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Transfers", to: "/cash/transfers" },
        { label: "Detail" },
      ]}
      viewportClassName="p-0 md:p-0"
      contentClassName="max-w-none"
    >
      <div className="flex flex-col min-h-full">{children}</div>
    </WorkspaceShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────

function StatusPill({ kind }: { kind: "posted" | "voided" | "reversal" }) {
  const tone =
    kind === "voided"
      ? "bg-bad-50 text-bad border-bad-100 line-through decoration-[1.5px]"
      : kind === "reversal"
        ? "bg-warn-50 text-warn border-warn-100"
        : "bg-ok-50 text-ok border-ok-100";
  const label = kind === "voided" ? "Voided" : kind === "reversal" ? "Reversal" : "Posted";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wider ${tone}`}
    >
      <span aria-hidden className="w-1 h-1 rounded-full bg-current" />
      {label}
    </span>
  );
}

function StateRibbon({
  tone,
  isReversed,
  isReversal,
  reversedByShort,
  reversesShort,
}: {
  tone: "ok" | "bad" | "warn";
  isReversed: boolean;
  isReversal: boolean;
  reversedByShort: string | null;
  reversesShort: string | null;
}) {
  const tones = {
    ok: { bg: "bg-ok-50 border-ok-100", ic: "bg-ok text-paper" },
    bad: { bg: "bg-bad-50 border-bad-100", ic: "bg-bad text-paper" },
    warn: { bg: "bg-warn-50 border-warn-100", ic: "bg-warn text-paper" },
  } as const;
  const t = tones[tone];

  return (
    <div
      role="status"
      className={`mb-3.5 px-4 py-3 rounded border ${t.bg} flex items-start gap-3 shadow-sm text-xs text-ink-2`}
    >
      <span aria-hidden className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${t.ic}`}>
        {isReversed ? (
          <RotateCcw size={16} />
        ) : isReversal ? (
          <RotateCcw size={16} />
        ) : (
          <Check size={16} strokeWidth={2.4} />
        )}
      </span>
      <div className="flex-1 min-w-0 leading-relaxed">
        {isReversed ? (
          <>
            <strong className="text-ink font-bold">Voided</strong> by reversal{" "}
            <code className="font-mono text-xs bg-ink/5 px-1 rounded text-ink-2">
              {reversedByShort}…
            </code>
            . The reversal is the canonical correction; this row stays as an append-only audit
            record.
          </>
        ) : isReversal ? (
          <>
            <strong className="text-ink font-bold">This is the reversal</strong> of{" "}
            <code className="font-mono text-xs bg-ink/5 px-1 rounded text-ink-2">
              {reversesShort}…
            </code>
            . The original row stays in the audit chain.
          </>
        ) : (
          <>
            <strong className="text-ink font-bold">Posted</strong> — clean reversal trail. You can
            <strong className="text-ink font-bold"> void within the audit window</strong> per{" "}
            <code className="font-mono text-xs bg-ink/5 px-1 rounded text-ink-2">ADR-0012 §3</code>
            ; voiding creates a new append-only reversal row, never edits this one.
          </>
        )}
      </div>
    </div>
  );
}

function PartyCell({
  direction,
  name,
  type,
  accountNumberLast4,
  imprestPaise,
}: {
  direction: "from" | "to";
  name: string;
  type: BankAccountType | null;
  accountNumberLast4: string | null;
  imprestPaise?: string | null;
}) {
  const typeChip =
    type === "bank"
      ? "bg-sky-50 text-sky-700 border-sky-100"
      : type === "cash"
        ? "bg-ok-50 text-ok border-ok-100"
        : type === "credit_card"
          ? "bg-warn-50 text-warn border-warn-100"
          : "bg-raised text-ink-2 border-line";

  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-bold uppercase tracking-wider text-muted mb-1.5 flex items-center gap-1.5">
        {direction === "from" ? (
          <ArrowLeft size={11} strokeWidth={2.2} className="text-muted" />
        ) : (
          <ArrowRight size={11} strokeWidth={2.2} className="text-muted" />
        )}
        {direction === "from" ? "From account" : "To account"}
      </div>
      <div className="text-base font-bold text-ink tracking-tight flex items-center gap-2 flex-wrap">
        {name}
        {type ? (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider border font-sans ${typeChip}`}
          >
            {type === "credit_card" ? "card" : type}
          </span>
        ) : null}
      </div>
      <div className="text-xs text-muted leading-relaxed">
        {accountNumberLast4 ? (
          <>
            Account <strong className="text-ink-2 font-medium">···{accountNumberLast4}</strong>
          </>
        ) : type === "cash" && imprestPaise ? (
          <>
            Imprest <span className="num text-ink-2">{paiseToInr(imprestPaise)}</span> · drawer
          </>
        ) : (
          <span className="text-muted">—</span>
        )}
      </div>
    </div>
  );
}

function DateCell({
  label,
  value,
  sub,
  muted,
  plainFont,
}: {
  label: string;
  value: string;
  sub?: string;
  muted?: boolean;
  plainFont?: boolean;
}) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-muted mb-0.5">{label}</div>
      <div
        className={`text-sm font-semibold ${plainFont ? "" : "font-mono"} ${muted ? "text-muted font-medium" : "text-ink"}`}
      >
        {value}
      </div>
      {sub ? <div className="mt-0.5 text-xs text-muted leading-snug">{sub}</div> : null}
    </div>
  );
}

function RailCard({
  title,
  mini,
  tone,
  children,
}: {
  title: string;
  mini?: string;
  tone?: "sky";
  children: React.ReactNode;
}) {
  const cardCls = tone === "sky" ? "bg-sky-50/40 border-sky-100" : "bg-paper border-line";
  const headTextCls = tone === "sky" ? "text-sky-700" : "text-muted";
  return (
    <div className={`border rounded shadow overflow-hidden ${cardCls}`}>
      <div
        className={`px-3.5 pt-3 pb-2 border-b ${tone === "sky" ? "border-sky-100" : "border-line"} flex items-center justify-between gap-2`}
      >
        <h4 className={`m-0 text-xs font-bold uppercase tracking-wider ${headTextCls}`}>
          {title}
        </h4>
        {mini ? <span className="text-xs text-muted">{mini}</span> : null}
      </div>
      <div className="px-3.5 py-3">{children}</div>
    </div>
  );
}

function GlRow({ side, label, amount }: { side: "dr" | "cr"; label: string; amount: string }) {
  const sideCls =
    side === "dr" ? "bg-ok-50 text-ok border-ok-100" : "bg-bad-50 text-bad border-bad-100";
  return (
    <div className="flex items-baseline justify-between text-xs">
      <span className="inline-flex items-center gap-1.5 text-ink-2">
        <span
          className={`inline-grid place-items-center w-4 h-4 rounded-full font-mono text-xs font-extrabold border ${sideCls}`}
        >
          {side === "dr" ? "Dr" : "Cr"}
        </span>
        <span className="truncate max-w-[140px]">{label}</span>
      </span>
      <span className="num font-semibold text-ink">{amount}</span>
    </div>
  );
}

function PartyRailCard({
  direction,
  account,
  amountPaise,
  balanceBefore,
  balanceAfter,
  imprestPct = null,
}: {
  direction: "from" | "to";
  account: {
    id: string;
    name: string;
    type: BankAccountType;
    accountNumberLast4: string | null;
    imprestAmountPaise: string | null;
  };
  amountPaise: string;
  balanceBefore: bigint | null;
  balanceAfter: bigint | null;
  imprestPct?: number | null;
}) {
  const isOut = direction === "from";
  const deltaLabel = isOut ? `−${paiseToInr(amountPaise)}` : `+${paiseToInr(amountPaise)}`;
  const headColor = isOut ? "text-bad" : "text-ok";
  const avaBg =
    account.type === "cash" ? "bg-ok" : account.type === "credit_card" ? "bg-warn" : "bg-sky-600";
  const Icon =
    account.type === "cash" ? Wallet : account.type === "credit_card" ? CreditCard : Banknote;
  const DeltaIcon = isOut ? TrendingDown : TrendingUp;

  return (
    <div className="bg-paper border border-line rounded shadow overflow-hidden">
      <div className="px-3.5 pt-3 pb-2 border-b border-line flex items-center justify-between gap-2">
        <h4 className="m-0 text-xs font-bold uppercase tracking-wider text-muted truncate">
          {isOut ? "From" : "To"} · {shorten(account.name, 16)}
        </h4>
        <span className={`text-xs font-semibold inline-flex items-center gap-1 ${headColor}`}>
          <DeltaIcon size={11} strokeWidth={2.4} />
          {deltaLabel}
        </span>
      </div>
      <div className="px-3.5 py-3">
        <div className="flex items-center gap-2.5 pb-2.5 border-b border-line mb-2.5">
          <div
            aria-hidden
            className={`w-9 h-9 rounded-sm text-paper grid place-items-center shrink-0 ${avaBg}`}
          >
            <Icon size={17} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-ink truncate">{account.name}</div>
            <div className="text-xs text-muted font-mono">
              {account.type}
              {account.accountNumberLast4 ? ` · ···${account.accountNumberLast4}` : ""}
              {account.type === "cash" && account.imprestAmountPaise
                ? ` · imprest ${paiseToInr(account.imprestAmountPaise)}`
                : ""}
            </div>
          </div>
        </div>

        <PartyField
          label="Balance before"
          value={balanceBefore !== null ? paiseToInr(balanceBefore) : "—"}
        />
        <PartyField
          label="Balance after"
          value={balanceAfter !== null ? paiseToInr(balanceAfter) : "—"}
        />
        <PartyField label="Delta" value={deltaLabel} tone={isOut ? "bad" : "ok"} />

        {imprestPct !== null ? (
          <div className="mt-2">
            <div className="flex justify-between text-xs text-muted mb-1">
              <span>Imprest now</span>
              <strong className="text-ink-2 font-semibold">{imprestPct}%</strong>
            </div>
            <div className="h-1.5 bg-bg rounded-full overflow-hidden">
              <div className="h-full bg-sky-600 rounded-full" style={{ width: `${imprestPct}%` }} />
            </div>
          </div>
        ) : null}

        <div className="mt-2.5 pt-2.5 border-t border-dashed border-line">
          <Link
            to="/cash/bank-accounts/$bankAccountId"
            params={{ bankAccountId: account.id }}
            className="inline-flex items-center gap-1 text-xs text-sky-700 font-semibold no-underline hover:underline"
          >
            View account
            <ArrowRight size={12} strokeWidth={2} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function PartyField({ label, value, tone }: { label: string; value: string; tone?: "ok" | "bad" }) {
  const valueCls = tone === "bad" ? "text-bad" : tone === "ok" ? "text-ok" : "text-ink";
  return (
    <div className="flex items-baseline gap-2.5 text-xs py-1 text-ink-2">
      <span className="text-muted text-xs min-w-[78px] shrink-0">{label}</span>
      <span className={`flex-1 text-right num font-semibold ${valueCls}`}>{value}</span>
    </div>
  );
}

function ImpactRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex justify-between items-baseline px-3.5 py-1.5 text-xs text-muted">
      <span>{label}</span>
      <span className={`num font-semibold ${muted ? "text-muted" : "text-ink-2"}`}>{value}</span>
    </div>
  );
}

function ImpactBoolRow({ label, yes }: { label: string; yes: boolean }) {
  const tone = yes ? "text-warn" : "text-ok";
  return (
    <div className="flex justify-between items-baseline px-3.5 py-1.5 text-xs text-muted">
      <span>{label}</span>
      <span className={`inline-flex items-center gap-1 font-semibold ${tone}`}>
        <Check size={12} strokeWidth={2.6} />
        {yes ? "YES" : "NO"}
      </span>
    </div>
  );
}

function RoutingStep({
  state,
  who,
  role,
  when,
  last,
}: {
  state: "done" | "now" | "todo";
  who: string;
  role: React.ReactNode;
  when: string;
  last?: boolean;
}) {
  const markerCls =
    state === "done"
      ? "bg-ok-50 border-ok text-ok"
      : state === "now"
        ? "bg-warn-50 border-warn text-warn"
        : "bg-paper border-line-2 text-muted";
  const whoCls = state === "now" ? "text-warn" : "text-ink";
  return (
    <li className="relative pb-3 pt-1.5 pl-7">
      {!last ? (
        <span aria-hidden className="absolute left-[9px] top-5 bottom-[-4px] w-[2px] bg-line" />
      ) : null}
      <span
        className={`absolute left-0 top-1 w-[18px] h-[18px] rounded-full grid place-items-center z-10 border-2 ${markerCls}`}
      >
        <Check size={9} strokeWidth={2.4} />
      </span>
      <div className="min-w-0">
        <div className={`text-xs font-semibold leading-snug ${whoCls}`}>{who}</div>
        <div className="text-xs text-muted">{role}</div>
        <div className="text-xs text-muted font-mono mt-0.5">{when}</div>
      </div>
    </li>
  );
}

function AuditMetaRow({ k, v, mutedValue }: { k: string; v: string; mutedValue?: boolean }) {
  return (
    <div className="flex items-baseline gap-2.5 py-1 text-xs text-ink-2 border-b border-dashed border-line last:border-0">
      <span className="text-muted min-w-[88px] shrink-0">{k}</span>
      <span
        className={`flex-1 text-right font-mono break-all ${mutedValue ? "text-muted" : "text-ink-2"}`}
      >
        {v}
      </span>
    </div>
  );
}

type LinkedCellTo = "/cash/bank-accounts/$bankAccountId" | "/records/audit-log";

function LinkedCell(props: {
  tone: "sky" | "ok" | "warn" | "muted";
  icon: React.ReactNode;
  kicker: string;
  value: string;
  sub: string;
  mutedValue?: boolean;
  to?: LinkedCellTo;
  params?: { bankAccountId: string };
}) {
  const iconCls = {
    sky: "bg-sky-50 border-sky-100 text-sky-700",
    ok: "bg-ok-50 border-ok-100 text-ok",
    warn: "bg-warn-50 border-warn-100 text-warn",
    muted: "bg-bg border-line text-muted",
  }[props.tone];

  const inner = (
    <div className="px-4 py-3.5 flex gap-3 items-start min-w-0 hover:bg-raised transition-colors border-r border-line last:border-r-0">
      <span className={`w-8 h-8 rounded-sm grid place-items-center shrink-0 border ${iconCls}`}>
        {props.icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold uppercase tracking-wider text-muted mb-0.5">
          {props.kicker}
        </div>
        <div
          className={`text-sm font-semibold ${props.mutedValue ? "text-muted" : "text-ink"} truncate`}
        >
          {props.value}
        </div>
        <div className="text-xs text-muted mt-0.5 truncate">{props.sub}</div>
      </div>
    </div>
  );

  if (props.to === "/cash/bank-accounts/$bankAccountId" && props.params) {
    return (
      <Link
        to="/cash/bank-accounts/$bankAccountId"
        params={props.params}
        className="block no-underline text-inherit"
      >
        {inner}
      </Link>
    );
  }
  if (props.to === "/records/audit-log") {
    return (
      <Link to="/records/audit-log" className="block no-underline text-inherit">
        {inner}
      </Link>
    );
  }
  return <div className="block">{inner}</div>;
}

function TimelineItem({
  tone,
  icon,
  what,
  when,
  who,
  body,
  last,
}: {
  tone: "ok" | "sky";
  icon: React.ReactNode;
  what: React.ReactNode;
  when: string;
  who: string;
  body: React.ReactNode;
  last?: boolean;
}) {
  const markerCls =
    tone === "ok" ? "bg-ok-50 border-ok text-ok" : "bg-sky-50 border-sky-600 text-sky-700";
  return (
    <li className="relative pt-1 pb-3.5 pl-9">
      {!last ? (
        <span aria-hidden className="absolute left-[26px] top-6 bottom-0 w-[2px] bg-line" />
      ) : null}
      <span
        className={`absolute left-[18px] top-1 w-[18px] h-[18px] rounded-full grid place-items-center z-10 border-2 ${markerCls}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-ink">{what}</span>
          <span className="text-xs text-muted font-mono">{when}</span>
          <span className="text-xs text-muted">· {who}</span>
        </div>
        <div className="text-xs text-muted leading-relaxed mt-0.5">{body}</div>
      </div>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────
// Helpers — local utilities (kept inline since this is the only caller).
// ────────────────────────────────────────────────────────────────────

function shorten(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1).trimEnd() + "…";
}

function shortenActor(actorId: string): string {
  if (!actorId) return "—";
  return actorId.length > 16 ? `${actorId.slice(0, 8)}…` : actorId;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function timeOnly(isoOrDate: string): string {
  return new Date(isoOrDate).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function isoStamp(iso: string): string {
  return formatDateTime(iso);
}

function weekdayHint(isoDate: string): string {
  const d = new Date(isoDate);
  const day = d.toLocaleDateString("en-IN", { weekday: "long" });
  return day;
}

function monthHeader(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" }).toUpperCase();
}

function isWithin24h(iso: string | null, nowMs: number): boolean {
  if (!iso) return false;
  return nowMs - new Date(iso).getTime() < 24 * 60 * 60 * 1000;
}
